/**
 * Carriage sets — a HARD physical constraint, not a slider.
 *
 * A set is a fixed rake for the whole day: four coaches cannot go out in the
 * morning and come back as seven in the evening — the seven-set must have been
 * sent out first. Sets chain between services exactly like engine diagrams
 * (arrive, turn round, work the next departure out of that station), and chains
 * may be CUT only at a carriage stable, so a big set can work the ends of the
 * day while a smaller one does the middle.
 *
 * Capacity comes ONLY from the assigned set. A chain with no set left in the
 * roster does not run — that is the physics, and reporting it is more useful
 * than quietly inventing stock.
 *
 * V3: sets may be restricted to a SERVICE GROUP (the shuttle unit stays on the
 * Whitby branch), and chains never span groups.
 */
import type {
  AssignedChain, CarriageSet, ClassDemand, CostModel, Departure, EngineParams,
  FitObjective, FleetCaps, Network, SetFormation, SetPlan,
} from './types'
import { capOf, runClasses } from './engine'
import { costFromDeps } from './costs'
import { groupKeyFor, locoTurnAt, journeyMin } from './network'

/**
 * The REAL fleet, capacities MEASURED (Chris, 2026-08-19): two Whitby sets at
 * 407 seats peak / 320 off-peak, one Internal set at 279 / ~220, and the
 * Autocar at 90 (not the old 48). The 64-seats-per-coach heuristic is dead —
 * `coaches` remains only as the vehicle count that drives carriage COSTS.
 * The Pullman dining set is rostered but inactive by default: dining is a
 * separate P&L (Diner & Charter) and its revenue is not in the daily-services
 * demand data, so daily-services scoring must not carry its costs either.
 */
export const DEFAULT_SETS: CarriageSet[] = [
  { code: 'W1',  coaches: 8, seats: 407, seatsOffPeak: 320, start: 0, active: true },
  { code: 'W2',  coaches: 7, seats: 407, seatsOffPeak: 320, start: 3, active: true },
  { code: 'INT', coaches: 5, seats: 279, seatsOffPeak: 220, start: 0, active: true },
  { code: 'P',   coaches: 3, seats: 110, seatsOffPeak: 110, start: 0, active: false },
  { code: 'R',   coaches: 0, seats: 90,  seatsOffPeak: 90,  start: 3, active: true },
]

/** A set's seats in the given formation. Legacy coach-count fallback ONLY for
 *  user-added sets with no measured capacity. */
export const setSeats = (s: CarriageSet, formation: SetFormation = 'peak'): number =>
  (formation === 'offpeak' ? s.seatsOffPeak ?? s.seats : s.seats) ?? capOf(s.coaches)

/** Mode B adds a dedicated shuttle unit stabled at Grosmont. */
export const SHUTTLE_SET: CarriageSet = {
  code: 'SH', coaches: 4, seats: 256, seatsOffPeak: 256, start: 3, active: true, group: 'shuttle',
}

export const modeSets = (mode: string): CarriageSet[] =>
  mode === 'grosmont'
    ? [...DEFAULT_SETS.map((s) => ({ ...s })), { ...SHUTTLE_SET }]
    : DEFAULT_SETS.map((s) => ({ ...s }))

interface RawChain { legs: Departure[]; station: number; readyAt: number; group: string }

/** Chain enabled legs geographically (tightest fit), within a service group. */
function buildChains(net: Network, deps: Departure[]): RawChain[] {
  const chains: RawChain[] = []
  const legs = deps
    .filter((d) => d.enabled && Number.isFinite(d.minutes))
    .sort((a, b) => a.minutes - b.minutes)
  for (const leg of legs) {
    const group = leg.group ?? groupKeyFor(net, leg.o, leg.d)
    const ready = chains
      .filter((c) => c.group === group && c.station === leg.o && c.readyAt <= leg.minutes)
      .sort((a, b) => b.readyAt - a.readyAt)[0]
    let c = ready
    if (!c) {
      c = { legs: [], station: leg.o, readyAt: 0, group }
      chains.push(c)
    }
    c.legs.push(leg)
    c.station = leg.d
    // The set turns with its train: measured turn + the real arrival when the
    // WTT gives one, same as the engine chainer (D1) — a 30-minute assumption
    // was spawning phantom rakes for every real 15–20 minute turn.
    c.readyAt = (leg.arrMin ?? leg.minutes + journeyMin(net, leg.o, leg.d)) + locoTurnAt(net, leg.d)
  }
  return chains
}

/**
 * Plan the day's carriage sets: chain the legs, try cutting chains at the
 * carriage stables, assign roster sets biggest-demand-first (honouring pins and
 * group restrictions), and keep whichever plan scores best on the objective.
 */
export function planSets(
  net: Network,
  classes: ClassDemand[],
  deps: Departure[],
  sets: CarriageSet[],
  p: EngineParams,
  objective: FitObjective,
  rates: CostModel,
  dieselLoco: number,
  fleet: FleetCaps,
  /** Passengers we are willing to leave unable to get home. Zero is a hard rule. */
  maxUnreturned = 0,
  /** Formation the day runs in — one formation per modelled day (Chris: sets
   *  stay fixed for a reasonable duration, never re-formed all day). */
  formation: SetFormation = 'peak'
): SetPlan {
  const active = sets.filter((s) => s.active)
  const w = classes.reduce((t, c) => t + c.weight, 0) || 1
  const base = buildChains(net, deps)

  // Size the sets by the big turns: what would each leg carry if capacity were
  // never the binding constraint? (512 seats — above any real rake.)
  const unconstrained = runClasses(net, classes, deps.map((d) => ({ ...d, coaches: 8, capacity: 512 })), p)
  const allocOf = (id: string) => unconstrained.perDep.find((x) => x.id === id)?.allocated ?? 0
  const peakOf = (legs: Departure[]) => Math.max(0, ...legs.map((l) => allocOf(l.id)))

  // Candidate cut points: an arrival into a carriage stable followed by a later
  // departure out of it.
  const cutPoints: { chain: number; at: number }[] = []
  base.forEach((c, ci) => {
    for (let i = 0; i + 1 < c.legs.length; i++) {
      if (net.carriageStables.includes(c.legs[i].d) && c.legs[i + 1].o === c.legs[i].d) {
        cutPoints.push({ chain: ci, at: i })
      }
    }
  })
  const usableCuts = cutPoints.slice(0, 5) // <=32 combos — plenty for a heritage day

  const applyTo = (plan: { chains: AssignedChain[] }): Departure[] =>
    deps.map((d) => {
      if (!d.enabled) return d
      const ch = plan.chains.find((c) => c.legs.some((l) => l.id === d.id))
      return ch?.set
        ? { ...d, coaches: ch.set.coaches, capacity: setSeats(ch.set, formation) }
        : { ...d, coaches: -1, capacity: 0 } // no set -> can't run
    })

  const evaluate = (plan: { chains: AssignedChain[] }): number => {
    const applied = applyTo(plan).map((d) => (d.coaches === -1 ? { ...d, enabled: false } : d))
    const r = runClasses(net, classes, applied, p)
    // Leaving people stranded is not a trade-off the optimiser may make on its
    // own: anything over the allowance is priced so far above any revenue that
    // no plan can buy its way past it.
    const strandPenalty = Math.max(0, r.unreturned - maxUnreturned) * 1e6
    if (objective !== 'profit') return r.revenue - strandPenalty
    const carriages = plan.chains.reduce((t, c) => t + (c.set?.coaches ?? 0), 0)
    const setsUsed = plan.chains.filter((c) => c.set).length
    const cost = costFromDeps(net, applied, rates, dieselLoco, fleet, carriages, setsUsed).total
    return r.revenue - cost * w - strandPenalty
  }

  const assign = (raw: RawChain[]): { chains: AssignedChain[]; warnings: string[] } => {
    const warnings: string[] = []
    const chains: AssignedChain[] = raw
      .map((c) => ({
        set: null as CarriageSet | null,
        legs: c.legs,
        origin: c.legs[0]?.o ?? 0,
        peak: peakOf(c.legs),
        group: c.group,
      }))
      .sort((a, b) => b.peak - a.peak)

    const free = [...active]
    // The Autocar is a self-propelled UNIT, not a rake a locomotive can haul:
    // it may only work a chain whose legs are railcar legs, and a loco-hauled
    // chain may never "be" the Autocar. Found by the plausibility harness —
    // the planner was giving the 90-seat railcar to a loco chain and stranding
    // 269 passengers on the real August timetable.
    const railcarChain = (ch: AssignedChain) =>
      ch.legs.every((l) => l.tractionPin === 'railcar' || l.coaches === 0)
    const eligible = (s: CarriageSet, ch: AssignedChain) =>
      (!s.group || s.group === ch.group) && ((s.coaches === 0) === railcarChain(ch))

    // Pins first — a pinned departure claims its set for the whole chain.
    for (const ch of chains) {
      const pin = ch.legs.map((l) => l.setPin).find(Boolean)
      if (!pin) continue
      const idx = free.findIndex((s) => s.code === pin && eligible(s, ch))
      if (idx >= 0) ch.set = free.splice(idx, 1)[0]
      else warnings.push(`Set ${pin} pinned twice, inactive, or not permitted on this route — chain from ${ch.legs[0].time} auto-assigned instead`)
    }
    // Then biggest demand gets the biggest eligible remaining set.
    for (const ch of chains) {
      if (ch.set) continue
      const idx = free
        .map((s, i) => ({ s, i }))
        .filter(({ s }) => eligible(s, ch))
        .sort((a, b) => setSeats(b.s, formation) - setSeats(a.s, formation))[0]?.i
      if (idx != null) ch.set = free.splice(idx, 1)[0]
      else warnings.push(`No carriage set left for the ${ch.legs[0].time} chain (${ch.legs.length} leg${ch.legs.length === 1 ? '' : 's'}) — those services can't run`)
    }

    for (const ch of chains) {
      if (!ch.set) continue
      if (ch.set.start !== ch.origin) {
        warnings.push(`Set ${ch.set.code} stables at ${net.stations[ch.set.start].name} but starts from ${net.stations[ch.origin].name} — needs overnight ECS positioning`)
      }
      const last = ch.legs[ch.legs.length - 1]
      if (last && !net.carriageStables.includes(last.d)) {
        warnings.push(`Set ${ch.set.code} ends the day at ${net.stations[last.d].name} — no carriage stable there; it needs a balancing return working (stock imbalance)`)
      }
    }
    return { chains, warnings }
  }

  let best: { chains: AssignedChain[]; warnings: string[]; score: number } | null = null
  const nCombos = 1 << usableCuts.length
  for (let mask = 0; mask < nCombos; mask++) {
    const cutsByChain = new Map<number, number[]>()
    for (let b = 0; b < usableCuts.length; b++) {
      if (mask & (1 << b)) {
        const cp = usableCuts[b]
        cutsByChain.set(cp.chain, [...(cutsByChain.get(cp.chain) ?? []), cp.at])
      }
    }
    const raw: RawChain[] = []
    base.forEach((c, ci) => {
      const cuts = (cutsByChain.get(ci) ?? []).sort((a, b) => a - b)
      let start = 0
      for (const at of cuts) {
        raw.push({ legs: c.legs.slice(start, at + 1), station: 0, readyAt: 0, group: c.group })
        start = at + 1
      }
      raw.push({ legs: c.legs.slice(start), station: 0, readyAt: 0, group: c.group })
    })
    const candidate = assign(raw.filter((r) => r.legs.length > 0))
    // Cuts that oversubscribe the roster help nobody.
    if (candidate.chains.some((c) => !c.set) && mask !== 0) continue
    const score = evaluate(candidate)
    if (!best || score > best.score) best = { ...candidate, score }
  }
  const chosen = best ?? assign(base)

  const byDep: Record<string, string> = {}
  for (const ch of chosen.chains) {
    if (!ch.set) continue
    for (const l of ch.legs) byDep[l.id] = ch.set.code
  }
  return {
    chains: chosen.chains,
    byDep,
    warnings: chosen.warnings,
    carriages: chosen.chains.reduce((t, c) => t + (c.set?.coaches ?? 0), 0),
    setsInTraffic: chosen.chains.filter((c) => c.set).length,
    formation,
  }
}

/** Rewrite departures so every leg's capacity is its assigned rake's MEASURED
 *  seats in the plan's formation. */
export function applySetPlan(deps: Departure[], plan: SetPlan): Departure[] {
  return deps.map((d) => {
    if (!d.enabled) return d
    const code = plan.byDep[d.id]
    if (!code) return { ...d, enabled: false }
    const ch = plan.chains.find((c) => c.set?.code === code && c.legs.some((l) => l.id === d.id))
    return ch?.set
      ? { ...d, coaches: ch.set.coaches, capacity: setSeats(ch.set, plan.formation) }
      : { ...d, enabled: false }
  })
}
